import { Collapse, Grid } from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useSetRecoilState } from "recoil";

import { pendingBookingsByLocationFilterState } from "../../../../../recoil";
import { IFormValuesPendingBookingsByLocationListDashboardFilter } from "../../../../../types";

import FilterActions from "../../../../../../../components/filter/filter-actions/FilterActions";
import FilterWrapper from "../../../../../../../components/filter/filter-wrapper/FilterWrapper";
import FormAutocompleteSegment from "../../_components/form-autocomplete-segment/FormAutocompleteSegment";
import FormAutocompleteProduct from "../../_components/form-autocomplete-product/FormAutocompleteProduct";
import FormAutocompleteCategory from "../../_components/form-autocomplete-category/FormAutocompleteCategory";
import FormAutocompleteLocation from "../../_components/form-autocomplete-location/FormAutocompleteLocation";
import TenantForm from "../../../../../../../components/multitenant/TenantForm";
import DealerForm from "../../../../../../../components/multitenant/DealerForm";
import { GlobalContext } from "../../../../../../../../../util/state/GlobalContextProvider";
import useFormTenantsDealers from "../../_components/form-tenants-dealers-hook/useFormTenantsDealers";

/**
 * Filter component of pending bookings by location
 */
const FilterPendingBksByLoc = () => {

    const {globalData} = useContext(GlobalContext);
    const setPendingBookingsByLocationFilter = useSetRecoilState(pendingBookingsByLocationFilterState);

    const form = useForm<IFormValuesPendingBookingsByLocationListDashboardFilter>({
        defaultValues: {
            segments: [],
            categories: [],
            products: [],
            pickupLocations: [],
            tenants: [],
            dealers: [],
        }
    });

    const tenantsWatch = useWatch({
        control: form.control,
        name: "tenants",
    });

    const dealersWatch = useWatch({
        control: form.control,
        name: "dealers",
    });

    const { setTenantsDealers } = useFormTenantsDealers();

    useEffect(() => {
        setTenantsDealers({ tenants: tenantsWatch, dealers: dealersWatch, })
    }, [tenantsWatch, dealersWatch]);

    const [expanded, setExpanded] = useState<boolean>(false);

    const createPendingListByLocationFilter = useCallback((data: IFormValuesPendingBookingsByLocationListDashboardFilter): any => ({
        segments: data.segments.map(s => s.code),
        categories: data.categories.map(c => c.code),
        products: data.products.map(p => p.code),
        pickupLocations: data.pickupLocations.map(pl => pl.id),
        tenants: data.tenants,
        dealers: data.dealers,
    }), []);

    const handleSearch = useCallback((data: IFormValuesPendingBookingsByLocationListDashboardFilter) => {
        setPendingBookingsByLocationFilter(createPendingListByLocationFilter(data));
        setExpanded(false);
    }, []);

    const handleReset = useCallback(() => {
        form.reset();
        setPendingBookingsByLocationFilter(createPendingListByLocationFilter(form.getValues()));
        setExpanded(false);
    }, []);

    const handleToggle = useCallback(() => {
        setExpanded(current => !current);
    }, [setExpanded]);

    useEffect(() => {
        if (globalData.user.legalEntities.length === 1) {
            form.setValue('tenants', globalData.user.legalEntities.map((elem: any) => elem.name));
        } else if (globalData.user.legalEntities.length > 1) {
            form.setValue('tenants', globalData.user.legalEntities.filter((elem: any) => elem.name !== 'all').map((elem: any) => elem.name));
            form.setValue('dealers', globalData.user.dealers.filter((elem: any) => elem.name !== 'all').map((elem: any) => elem.name));
        }

        setPendingBookingsByLocationFilter(createPendingListByLocationFilter(form.getValues()));
    }, []);

    return (
        <FilterWrapper>
            <FormProvider {...form}>
                <Grid container columnSpacing={5} alignItems="center">
                    <Grid item xs={12} md={6} lg={3}>
                        <TenantForm
                            name="tenants"
                            multiple={true}
                            onChange={() => form.setValue("dealers", [])}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                        <DealerForm
                            name="dealers"
                            multiple={true}
                            tenants={tenantsWatch}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                        <FormAutocompleteSegment />
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                        <FormAutocompleteCategory />
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                        <FormAutocompleteProduct />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <FilterActions
                            expanded={expanded}
                            onSearch={form.handleSubmit(handleSearch)}
                            onReset={handleReset}
                            onToggle={handleToggle}
                        />
                    </Grid>
                </Grid>
                <Collapse in={expanded} timeout="auto" sx={{ width: '100%' }}>
                    <Grid container columnSpacing={5} rowSpacing={2} pt={2}>
                        <Grid item xs={12} md={6} lg={3}>
                            <FormAutocompleteLocation />
                        </Grid>
                    </Grid>
                </Collapse>
            </FormProvider>
        </FilterWrapper>
    );
}

export default FilterPendingBksByLoc;
